import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const WAITLIST_PLACEHOLDER = 47;

const INITIAL_ROWS = [
  { id: "1", email: "maya.ellison@email.com", city: "Richmond", message: "Happy to test Expo Go this month.", date: "Oct 24, 2026", status: "New" },
  { id: "2", email: "james.whitaker@email.com", city: "Nearby", message: "", date: "Oct 24, 2026", status: "New" },
  { id: "3", email: "elena.cho@email.com", city: "Richmond", message: "Can give feedback after work.", date: "Oct 25, 2026", status: "Accepted" },
  { id: "4", email: "owen.hart@email.com", city: "Somewhere else", message: "Moving to RVA in December.", date: "Oct 25, 2026", status: "New" },
  { id: "5", email: "priya.nair@email.com", city: "Richmond", message: "", date: "Oct 26, 2026", status: "New" },
  { id: "6", email: "chris.lane@email.com", city: "Nearby", message: "Midlothian. Ready when you are.", date: "Oct 26, 2026", status: "Accepted" },
  { id: "7", email: "sofia.reed@email.com", city: "Richmond", message: "Prefer evenings.", date: "Oct 26, 2026", status: "New" },
  { id: "8", email: "marcus.bell@email.com", city: "Richmond", message: "", date: "Oct 27, 2026", status: "New" },
];

const FILTERS = ["All", "Richmond", "Nearby", "Accepted"];

const matchesFilter = (row, filter) => {
  if (filter === "All") return true;
  if (filter === "Accepted") return row.status === "Accepted";
  return row.city === filter;
};

const BetaAdminPage = () => {
  const [rows, setRows] = useState(INITIAL_ROWS);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(() => new Set());

  const visible = useMemo(() => rows.filter((row) => matchesFilter(row, filter)), [rows, filter]);
  const richmondArea = rows.filter((row) => row.city === "Richmond" || row.city === "Nearby").length;

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const ids = visible.map((row) => row.id);
    const allOn = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const markAccepted = () => {
    if (selected.size === 0) return;
    setRows((prev) =>
      prev.map((row) => (selected.has(row.id) ? { ...row, status: "Accepted" } : row)),
    );
    setSelected(new Set());
  };

  const exportCsv = () => {
    const header = ["Email", "City", "Message", "Date", "Status"];
    const lines = [
      header.join(","),
      ...visible.map((row) =>
        [row.email, row.city, row.message, row.date, row.status]
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "community-hearts-beta.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F7F3EA] font-sans text-[#1A120C]" data-testid="beta-admin-page">
      <header className="border-b border-[#C4A36A]/25 bg-[#F7F3EA]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-3" data-testid="beta-admin-logo">
            <img src="/assets/hero-crest.png?v=2" alt="" className="h-10 w-auto" />
            <span className="font-serif text-sm font-medium uppercase tracking-[0.22em] text-[#A08448]">
              Community Hearts
            </span>
          </Link>
          <Link
            to="/"
            className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A08448] transition-colors hover:text-[#7A5A14]"
            data-testid="beta-admin-back-link"
          >
            Back to the site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 md:px-10 md:py-16">
        <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-[#A08448]" data-testid="beta-admin-kicker">
          Admin · Beta
        </p>
        <h1
          className="mt-4 font-serif text-4xl font-medium tracking-tight text-[#1A120C] md:text-5xl"
          data-testid="beta-admin-headline"
        >
          Beta Dashboard
        </h1>
        <span className="mt-8 block h-px w-16 bg-[#C4A36A]" aria-hidden="true" />

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {[
            { label: "Beta requests", value: rows.length, id: "requests" },
            { label: "Waitlist", value: WAITLIST_PLACEHOLDER, id: "waitlist" },
            { label: "Richmond-area", value: richmondArea, id: "richmond" },
          ].map((stat) => (
            <div
              key={stat.id}
              className="rounded-3xl border border-[#C4A36A]/35 bg-[#FBF8F3] px-6 py-6"
              data-testid={`beta-admin-stat-${stat.id}`}
            >
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-[#A08448]">{stat.label}</p>
              <p className="mt-3 font-serif text-4xl tracking-tight text-[#1A120C]">{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="beta-admin-filters">
            {FILTERS.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                data-testid={`beta-admin-filter-${item.toLowerCase()}`}
                className={`rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                  filter === item
                    ? "border-[#C0965A] bg-[#C0965A] text-[#FFF8E8]"
                    : "border-[#C4A36A] text-[#A08448] hover:bg-[#C4A36A]/10"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={markAccepted}
              disabled={selected.size === 0}
              data-testid="beta-admin-mark-accepted"
              className="rounded-full border border-[#C4A36A] px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#A08448] transition-colors hover:bg-[#C4A36A]/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Mark accepted
            </button>
            <button
              type="button"
              onClick={exportCsv}
              data-testid="beta-admin-export"
              className="rounded-full bg-[#C0965A] px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#FFF8E8] transition-colors hover:bg-[#B0894C]"
            >
              Export
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-3xl border border-[#C4A36A]/35 bg-[#FBF8F3]">
          <table className="min-w-full text-left" data-testid="beta-admin-table">
            <thead>
              <tr className="border-b border-[#C4A36A]/25 text-[11px] font-medium uppercase tracking-[0.16em] text-[#A08448]">
                <th className="px-5 py-4">
                  <input
                    type="checkbox"
                    aria-label="Select all visible rows"
                    checked={visible.length > 0 && visible.every((row) => selected.has(row.id))}
                    onChange={toggleAllVisible}
                    className="accent-[#C4A36A]"
                    data-testid="beta-admin-select-all"
                  />
                </th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">City</th>
                <th className="px-5 py-4">Message</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.id} className="border-b border-[#C4A36A]/15 last:border-0" data-testid={`beta-admin-row-${row.id}`}>
                  <td className="px-5 py-4">
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.email}`}
                      checked={selected.has(row.id)}
                      onChange={() => toggleRow(row.id)}
                      className="accent-[#C4A36A]"
                    />
                  </td>
                  <td className="px-5 py-4 font-serif text-sm text-[#1A120C]">{row.email}</td>
                  <td className="px-5 py-4 font-serif text-sm text-[#3A2C20]">{row.city}</td>
                  <td className="max-w-xs px-5 py-4 font-serif text-sm text-[#5C4632]">{row.message || "—"}</td>
                  <td className="whitespace-nowrap px-5 py-4 font-serif text-sm text-[#3A2C20]">{row.date}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                        row.status === "Accepted"
                          ? "bg-[#C4A36A]/20 text-[#7A5A14]"
                          : "border border-[#C4A36A]/40 text-[#A08448]"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-serif text-xs italic text-[#8C7040]">Placeholder rows. No live data yet.</p>
      </main>
    </div>
  );
};

export default BetaAdminPage;
